// src/components/groups/JoinedGroupCard.jsx
// Horizontal card component for groups the user has joined or suggested groups
// Redesigned January 2026: Image left (4:3), introduction right, headline below
// Compact margins for tighter layout

import React from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { tokens } from '../../styles/designTokens';

const JoinedGroupCard = ({ group, onClick, onJoin, isSuggested = false }) => {
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('groups');
  // Truncate description for introduction text
  const introduction = group.description
    ? group.description.length > 100
      ? group.description.substring(0, 100) + '...'
      : group.description
    : t('no_description');

  return (
    <div
      onClick={isSuggested ? undefined : () => onClick(group)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        width: isSuggested ? '240px' : '300px',
        flexShrink: 0,
        cursor: isSuggested ? 'default' : 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s'
      }}
      onMouseEnter={isSuggested ? undefined : (e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={isSuggested ? undefined : (e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
      }}
    >
      {/* Card Body - Image left, Introduction right */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        padding: '6px',
        gap: '6px'
      }}>
        {/* Image - 4:3 ratio */}
        <div style={{
          width: '110px',
          height: '82px',
          flexShrink: 0,
          background: '#f3f4f6',
          borderRadius: '6px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {group.logo_url ? (
            <img
              src={group.logo_url}
              alt={group.name}
              loading="lazy"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <span style={{ color: '#9ca3af', fontSize: '10px' }}>{t('no_image')}</span>
          )}
        </div>

        {/* Introduction text */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isSuggested ? 'flex-start' : 'center',
          minWidth: 0,
          padding: '2px 0'
        }}>
          <p style={{
            fontSize: '12px',
            color: '#666',
            lineHeight: '1.35',
            margin: 0,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: isSuggested ? 4 : 3,
            WebkitBoxOrient: 'vertical'
          }}>
            {introduction}
          </p>
          {/* Visit Group button positioned in suggested cards */}
          {isSuggested && (
            <button
              onClick={() => onClick(group)}
              style={{
                marginTop: '6px',
                padding: '5px 8px',
                background: theme.primary,
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                alignSelf: 'flex-start'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              {t('visit_group_button')}
            </button>
          )}
        </div>
      </div>

      {/* Headline */}
      <div style={{
        padding: '4px 6px',
        borderTop: '1px solid #eee'
      }}>
        <h3 style={{
          fontSize: '14px',
          fontWeight: '600',
          margin: 0,
          color: '#1e293b',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {group.name}
        </h3>
        {/* A closing group a member still sees (Roadmap #103): its date, as on the owner's card. */}
        {group.deleted_at && (
          <div style={{ fontSize: '11px', color: tokens.colors.notificationAlert, marginTop: '2px' }}>
            {group.deletion_scheduled_at
              ? t('closing_date', { date: new Date(group.deletion_scheduled_at).toLocaleDateString(formattingLocale) })
              : t('closing_notice_undated')}
          </div>
        )}
      </div>

    </div>
  );
};

export default JoinedGroupCard;
